import * as React from "react";
import { cn } from "../lib/cn";

export type ScrollEdges = { start: boolean; end: boolean };

/** Which edges have hidden content beyond them. 1px slack absorbs sub-pixel scroll positions. */
export function overflowEdges(scrollLeft: number, clientWidth: number, scrollWidth: number): ScrollEdges {
  const left = Math.abs(scrollLeft);
  return { start: left > 1, end: left + clientWidth < scrollWidth - 1 };
}

export interface HScrollProps extends React.ComponentProps<"div"> {
  /**
   * Which edges may fade. `end` only suits content with a sticky first column, which a
   * start fade would dim.
   */
  fade?: "both" | "end";
  /** Class for the inner scrolling element (the outer element only carries the fade). */
  viewportClassName?: string;
}

/**
 * Horizontal scroller with a fade on each edge that has more content beyond it — the cue that a
 * row of tabs, a wide table or a chart continues sideways. The fade is a mask, so it works on
 * any surface colour; it disappears at the ends, and with no overflow there is no fade at all.
 */
export function HScroll({ fade = "both", className, viewportClassName, children, ...props }: HScrollProps) {
  const ref = React.useRef<HTMLDivElement>(null);
  const [edges, setEdges] = React.useState<ScrollEdges>({ start: false, end: false });

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const next = overflowEdges(el.scrollLeft, el.clientWidth, el.scrollWidth);
      setEdges((prev) => (prev.start === next.start && prev.end === next.end ? prev : next));
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    for (const child of Array.from(el.children)) ro.observe(child);
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, []);

  const start = fade === "both" && edges.start;
  const end = edges.end;
  return (
    <div
      data-slot="h-scroll"
      data-overflow-start={edges.start || undefined}
      data-overflow-end={edges.end || undefined}
      className={cn(
        "relative min-w-0",
        start && end ? "fade-x-both" : start ? "fade-x-start" : end ? "fade-x-end" : undefined,
        className,
      )}
    >
      <div ref={ref} className={cn("overflow-x-auto overscroll-x-contain", viewportClassName)} {...props}>
        {children}
      </div>
    </div>
  );
}
